import type {
  PromotionBenefit,
  PromotionCatalogRef,
  PromotionConditionNode,
  PromotionRuntimeSnapshot,
} from "../gateway/promotion-runtime-client";
import {
  localCartSaleFingerprint,
  type LocalAppliedPromotion,
  type LocalPromotionApplicationCoverage,
  type LocalCartDraft,
  type LocalSaleQuote,
  type LocalStaffClaimPromotion,
} from "../local-store/cart-state";

export type LocalPromotionResolution = {
  quote: Omit<LocalSaleQuote, "cartFingerprint">;
  blockedReasonCodes: string[];
  requiresOnlineResolution: boolean;
};

type ResolverLine = {
  lineId: string;
  menuItemId: string;
  menuVariantId: string | null;
  menuCategoryId: string | null;
  variantOptionIds: string[];
  modifierGroupIds: string[];
  modifierOptionIds: string[];
  quantity: number;
  baseUnitPrice: number;
  eligibleMinor: number;
};

type Candidate = {
  runtime: PromotionRuntimeSnapshot;
  requestedMinor: number;
  allocations: Map<string, number>;
  applicationCoverage: LocalPromotionApplicationCoverage;
};

function moneyFactor(currencyScale: number) {
  return 10 ** currencyScale;
}

function toMinor(value: number, currencyScale: number) {
  return Math.round((value + Number.EPSILON) * moneyFactor(currencyScale));
}

function fromMinor(value: number, currencyScale: number) {
  return value / moneyFactor(currencyScale);
}

function positiveNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : Number(value);
}

function isPositiveInteger(value: unknown) {
  const number = positiveNumber(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function isoDay(weekday: string) {
  return ({ Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 } as Record<string, number>)[weekday] ?? 0;
}

function zonedParts(at: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const day = isoDay(get("weekday"));
  const minutes = Number(get("hour")) * 60 + Number(get("minute")) + Number(get("second")) / 60;
  return { day, minutes };
}

function timeMinutes(value: string) {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function scheduleEligible(runtime: PromotionRuntimeSnapshot, evaluationAt: Date) {
  const schedule = runtime.schedule_document ?? {};
  const windows = schedule.time_windows ?? [];
  const days = schedule.days_of_week ?? [];
  if (!windows.length && !days.length) return true;

  const local = zonedParts(evaluationAt, runtime.organization_timezone || "UTC");
  if (!windows.length) return days.includes(local.day);
  const previousDay = local.day === 1 ? 7 : local.day - 1;

  for (const window of windows) {
    const start = timeMinutes(window.start);
    const end = timeMinutes(window.end);
    if (start < end) {
      const dayOk = !days.length || days.includes(local.day);
      if (dayOk && local.minutes >= start && local.minutes < end) return true;
    } else if (start > end) {
      if (local.minutes >= start) {
        const dayOk = !days.length || days.includes(local.day);
        if (dayOk) return true;
      } else if (local.minutes < end) {
        const dayOk = !days.length || days.includes(previousDay);
        if (dayOk) return true;
      }
    }
  }
  return false;
}

function catalogRefMatchesLine(ref: PromotionCatalogRef, line: ResolverLine) {
  switch (ref.reference_type) {
    case "MENU_ITEM": return ref.reference_id === line.menuItemId;
    case "SELLABLE_VARIANT": return ref.reference_id === line.menuVariantId;
    case "MENU_CATEGORY": return ref.reference_id === line.menuCategoryId;
    case "MODIFIER_GROUP": return line.modifierGroupIds.includes(ref.reference_id);
    case "MODIFIER_OPTION": return line.modifierOptionIds.includes(ref.reference_id);
    case "VARIANT_OPTION": return line.variantOptionIds.includes(ref.reference_id);
    default: return false;
  }
}

function lineMatchesPoolEligibility(runtime: PromotionRuntimeSnapshot, poolCode: string, line: ResolverLine) {
  const pool = poolCode.toUpperCase();
  const rules = (runtime.pool_eligibility_rules ?? [])
    .filter((rule) => (rule.pool_code ?? "").toUpperCase() === pool)
    .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100));

  for (const rule of rules) {
    const code = (rule.rule_code ?? "").toUpperCase();
    if (code === "BASE_UNIT_PRICE_GTE") {
      const minimum = positiveNumber(rule.params?.amount);
      if (!Number.isFinite(minimum) || !Number.isFinite(line.baseUnitPrice) || line.baseUnitPrice < minimum) {
        return false;
      }
      continue;
    }
    return false;
  }
  return true;
}

function lineMatchesPool(runtime: PromotionRuntimeSnapshot, poolCode: string, line: ResolverLine) {
  const pool = poolCode.toUpperCase();
  const refs = (runtime.catalog_refs ?? []).filter(
    (ref) => (ref.pool_code ?? "").toUpperCase() === pool,
  );
  const includeMatched = refs.some(
    (ref) => (ref.match_mode ?? "INCLUDE").toUpperCase() === "INCLUDE" && catalogRefMatchesLine(ref, line),
  );
  const excludeMatched = refs.some(
    (ref) => (ref.match_mode ?? "INCLUDE").toUpperCase() === "EXCLUDE" && catalogRefMatchesLine(ref, line),
  );
  return includeMatched && !excludeMatched && lineMatchesPoolEligibility(runtime, poolCode, line);
}

function conditionMatches(node: PromotionConditionNode, runtime: PromotionRuntimeSnapshot, lines: ResolverLine[], currencyScale: number): boolean {
  if (node.kind === "GROUP") {
    const children = node.children ?? [];
    const result = node.operator === "AND"
      ? children.every((child) => conditionMatches(child, runtime, lines, currencyScale))
      : children.some((child) => conditionMatches(child, runtime, lines, currencyScale));
    return node.negate ? !result : result;
  }

  const params = node.params ?? {};
  const code = (node.code ?? "").toUpperCase();
  let result = false;
  if (code === "ALWAYS_TRUE") {
    result = true;
  } else if (code === "CART_SUBTOTAL_GTE") {
    result = lines.reduce((sum, line) => sum + line.eligibleMinor, 0) >= toMinor(positiveNumber(params.amount), currencyScale);
  } else if (code === "CART_TOTAL_QUANTITY_GTE") {
    result = lines.reduce((sum, line) => sum + line.quantity, 0) >= positiveNumber(params.quantity);
  } else if (code === "CATALOG_POOL_PRESENT") {
    result = lines.some((line) => lineMatchesPool(runtime, String(params.pool_code ?? ""), line));
  } else if (code === "POOL_QUANTITY_GTE") {
    result = lines
      .filter((line) => lineMatchesPool(runtime, String(params.pool_code ?? ""), line))
      .reduce((sum, line) => sum + line.quantity, 0) >= positiveNumber(params.quantity);
  } else if (code === "POOL_SUBTOTAL_GTE") {
    result = lines
      .filter((line) => lineMatchesPool(runtime, String(params.pool_code ?? ""), line))
      .reduce((sum, line) => sum + line.eligibleMinor, 0) >= toMinor(positiveNumber(params.amount), currencyScale);
  }
  return node.negate ? !result : result;
}

function proportionalAllocate(remaining: Map<string, number>, lineIds: string[], desiredMinor: number) {
  const targets = [...new Set(lineIds)].sort();
  const total = targets.reduce((sum, id) => sum + Math.max(0, remaining.get(id) ?? 0), 0);
  const desired = Math.min(Math.max(0, Math.round(desiredMinor)), total);
  const allocations = new Map<string, number>();
  if (!desired || !total) return allocations;

  const rows = targets.map((lineId) => {
    const available = Math.max(0, remaining.get(lineId) ?? 0);
    const raw = desired * available / total;
    const base = Math.floor(raw);
    return { lineId, available, raw, base, fraction: raw - base };
  });
  let remainder = desired - rows.reduce((sum, row) => sum + row.base, 0);
  rows.sort((a, b) => (b.fraction - a.fraction) || a.lineId.localeCompare(b.lineId));
  for (const row of rows) {
    const add = remainder > 0 ? 1 : 0;
    const amount = Math.min(row.available, row.base + add);
    if (add) remainder -= 1;
    if (amount > 0) allocations.set(row.lineId, amount);
  }
  return allocations;
}

function applyAllocations(remaining: Map<string, number>, allocations: Map<string, number>) {
  for (const [lineId, amount] of allocations) {
    remaining.set(lineId, Math.max(0, (remaining.get(lineId) ?? 0) - amount));
  }
}

function mergeAllocations(target: Map<string, number>, source: Map<string, number>) {
  for (const [lineId, amount] of source) target.set(lineId, (target.get(lineId) ?? 0) + amount);
}

function fixedAmountPerUnitAllocate(
  lines: ResolverLine[],
  remaining: Map<string, number>,
  targetLineIds: string[],
  amountMinor: number,
) {
  const target = new Set(targetLineIds);
  const allocations = new Map<string, number>();
  const perUnit = Math.max(0, Math.round(amountMinor));
  for (const line of lines) {
    if (!target.has(line.lineId)) continue;
    if (!Number.isInteger(line.quantity) || line.quantity <= 0 || line.quantity > 1000) {
      throw new Error("NON_INTEGER_QUANTITY_NOT_SUPPORTED");
    }
    const available = Math.max(0, remaining.get(line.lineId) ?? 0);
    const amount = Math.min(available, perUnit * line.quantity);
    if (amount > 0) allocations.set(line.lineId, amount);
  }
  return allocations;
}

function cheapestUnitAllocate(
  lines: ResolverLine[],
  remaining: Map<string, number>,
  targetLineIds: string[],
  units: number,
  percent: number,
) {
  const target = new Set(targetLineIds);
  const unitRows: Array<{ key: string; lineId: string; minor: number }> = [];
  for (const line of lines) {
    if (!target.has(line.lineId) || !Number.isInteger(line.quantity) || line.quantity <= 0 || line.quantity > 1000) continue;
    const lineRemaining = Math.max(0, remaining.get(line.lineId) ?? 0);
    const base = Math.floor(lineRemaining / line.quantity);
    let remainder = lineRemaining - base * line.quantity;
    for (let index = 1; index <= line.quantity; index += 1) {
      const minor = base + (remainder-- > 0 ? 1 : 0);
      unitRows.push({ key: `${line.lineId}#${String(index).padStart(6, "0")}`, lineId: line.lineId, minor });
    }
  }
  unitRows.sort((a, b) => (a.minor - b.minor) || a.key.localeCompare(b.key));
  const allocations = new Map<string, number>();
  for (const unit of unitRows.slice(0, Math.max(0, units))) {
    const amount = Math.min(unit.minor, Math.round(unit.minor * percent / 100));
    allocations.set(unit.lineId, (allocations.get(unit.lineId) ?? 0) + amount);
  }
  return allocations;
}

function bundlePriceAllocate(
  lines: ResolverLine[],
  remaining: Map<string, number>,
  targetLineIds: string[],
  requiredQty: number,
  bundlePriceMinor: number,
  applications: number,
) {
  const target = new Set(targetLineIds);
  const unitRows: Array<{ key: string; lineId: string; minor: number }> = [];
  for (const line of lines) {
    if (!target.has(line.lineId)) continue;
    if (!Number.isInteger(line.quantity) || line.quantity <= 0 || line.quantity > 1000) {
      throw new Error("NON_INTEGER_QUANTITY_NOT_SUPPORTED");
    }
    const lineRemaining = Math.max(0, remaining.get(line.lineId) ?? 0);
    const base = Math.floor(lineRemaining / line.quantity);
    let remainder = lineRemaining - base * line.quantity;
    for (let index = 1; index <= line.quantity; index += 1) {
      unitRows.push({
        key: `${line.lineId}#${String(index).padStart(6, "0")}`,
        lineId: line.lineId,
        minor: base + (remainder-- > 0 ? 1 : 0),
      });
    }
  }
  const selected = unitRows
    .sort((a, b) => (b.minor - a.minor) || a.key.localeCompare(b.key))
    .slice(0, Math.max(0, requiredQty * applications));
  const selectedTotal = selected.reduce((sum, unit) => sum + unit.minor, 0);
  const desired = Math.max(0, selectedTotal - Math.max(0, Math.round(bundlePriceMinor)) * applications);
  if (!desired || !selectedTotal) return new Map<string, number>();

  const rows = selected.map((unit) => {
    const raw = desired * unit.minor / selectedTotal;
    const base = Math.floor(raw);
    return { ...unit, base, fraction: raw - base };
  });
  let remainder = desired - rows.reduce((sum, row) => sum + row.base, 0);
  rows.sort((a, b) => (b.fraction - a.fraction) || a.key.localeCompare(b.key));
  const allocations = new Map<string, number>();
  for (const row of rows) {
    const add = remainder > 0 ? 1 : 0;
    const amount = Math.min(row.minor, row.base + add);
    if (add) remainder -= 1;
    if (amount > 0) allocations.set(row.lineId, (allocations.get(row.lineId) ?? 0) + amount);
  }
  return allocations;
}

function targetLineIds(runtime: PromotionRuntimeSnapshot, lines: ResolverLine[], target: string, poolCode?: string) {
  return target === "ORDER"
    ? lines.map((line) => line.lineId).sort()
    : lines.filter((line) => lineMatchesPool(runtime, poolCode ?? "", line)).map((line) => line.lineId).sort();
}

type CoverageUnitRow = {
  key: string;
  lineId: string;
  unitNo: number;
  minor: number;
};

function coverageUnitRows(lines: ResolverLine[], lineIds: string[]) {
  const target = new Set(lineIds);
  const rows: CoverageUnitRow[] = [];
  for (const line of lines) {
    if (!target.has(line.lineId)) continue;
    if (!Number.isInteger(line.quantity) || line.quantity <= 0 || line.quantity > 1000) return null;
    const base = Math.floor(line.eligibleMinor / line.quantity);
    let remainder = line.eligibleMinor - base * line.quantity;
    for (let index = 1; index <= line.quantity; index += 1) {
      rows.push({
        key: `${line.lineId}#${String(index).padStart(6, "0")}`,
        lineId: line.lineId,
        unitNo: index,
        minor: base + (remainder-- > 0 ? 1 : 0),
      });
    }
  }
  return rows;
}

function groupCoverageUnits(rows: CoverageUnitRow[]) {
  const grouped = new Map<string, number[]>();
  for (const row of rows) {
    const current = grouped.get(row.lineId) ?? [];
    if (!current.includes(row.unitNo)) current.push(row.unitNo);
    grouped.set(row.lineId, current);
  }
  return [...grouped.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([lineId, unitNos]) => ({
      lineId,
      unitNos: unitNos.sort((a, b) => a - b),
      coveredQuantity: unitNos.length,
    }));
}

function notReadyCoverage(benefitCode: string): LocalPromotionApplicationCoverage {
  return {
    ready: false,
    scope: "UNIT",
    benefitCode,
    appliedApplicationCount: 0,
    coveredUnits: [],
  };
}

function resolveApplicationCoverage(
  runtime: PromotionRuntimeSnapshot,
  lines: ResolverLine[],
): LocalPromotionApplicationCoverage {
  const benefits = runtime.benefit_document ?? [];
  const benefit = benefits[0];
  const code = String(benefit?.code ?? "").toUpperCase();
  if (!benefit || benefits.length !== 1) return notReadyCoverage(code || "MULTI_BENEFIT");
  const params = benefit.params ?? {};

  if (
    (code === "PERCENT_OFF" || code === "FIXED_AMOUNT_OFF") &&
    String(params.target ?? "").toUpperCase() === "ORDER"
  ) {
    return {
      ready: true,
      scope: "ORDER",
      benefitCode: code,
      appliedApplicationCount: 1,
      coveredUnits: [],
    };
  }

  if (code === "BUY_X_GET_Y") {
    const triggerPool = String(params.trigger_pool_code ?? "");
    const rewardPool = String(params.reward_pool_code ?? "");
    const triggerQty = isPositiveInteger(params.trigger_qty);
    const rewardQty = isPositiveInteger(params.reward_qty);
    if (!triggerPool || !rewardPool || !triggerQty || !rewardQty) return notReadyCoverage(code);

    const triggerIds = targetLineIds(runtime, lines, "POOL", triggerPool);
    const rewardIds = targetLineIds(runtime, lines, "POOL", rewardPool);
    const triggerRows = coverageUnitRows(lines, triggerIds);
    const rewardRows = coverageUnitRows(lines, rewardIds);
    if (!triggerRows || !rewardRows) return notReadyCoverage(code);

    const triggerTotal = triggerRows.length;
    const rewardTotal = rewardRows.length;
    let applications = triggerPool.toUpperCase() === rewardPool.toUpperCase()
      ? Math.floor(triggerTotal / (triggerQty + rewardQty))
      : Math.min(Math.floor(triggerTotal / triggerQty), Math.floor(rewardTotal / rewardQty));
    if (params.repeat === false) applications = Math.min(applications, 1);
    const maxApplications = isPositiveInteger(runtime.limit_document?.max_applications_per_order);
    if (maxApplications) applications = Math.min(applications, maxApplications);
    if (applications <= 0) return notReadyCoverage(code);

    const rewardCount = applications * rewardQty;
    const rewards = [...rewardRows]
      .sort((a, b) => (a.minor - b.minor) || a.key.localeCompare(b.key))
      .slice(0, rewardCount);
    if (rewards.length !== rewardCount) return notReadyCoverage(code);

    const rewardKeys = new Set(rewards.map((row) => row.key));
    const triggerCount = applications * triggerQty;
    const triggers = [...triggerRows]
      .filter((row) => !rewardKeys.has(row.key))
      .sort((a, b) => (b.minor - a.minor) || a.key.localeCompare(b.key))
      .slice(0, triggerCount);
    if (triggers.length !== triggerCount) return notReadyCoverage(code);

    return {
      ready: true,
      scope: "UNIT",
      benefitCode: code,
      appliedApplicationCount: applications,
      coveredUnits: groupCoverageUnits([...rewards, ...triggers]),
    };
  }

  if (code === "BUNDLE_PRICE") {
    const pool = String(params.pool_code ?? "");
    const requiredQty = isPositiveInteger(params.required_qty);
    if (!pool || !requiredQty) return notReadyCoverage(code);
    const ids = targetLineIds(runtime, lines, "POOL", pool);
    const rows = coverageUnitRows(lines, ids);
    if (!rows) return notReadyCoverage(code);
    let applications = Math.floor(rows.length / requiredQty);
    if (params.repeat === false) applications = Math.min(applications, 1);
    const maxApplications = isPositiveInteger(runtime.limit_document?.max_applications_per_order);
    if (maxApplications) applications = Math.min(applications, maxApplications);
    if (applications <= 0) return notReadyCoverage(code);
    const selected = [...rows]
      .sort((a, b) => (b.minor - a.minor) || a.key.localeCompare(b.key))
      .slice(0, applications * requiredQty);
    return {
      ready: selected.length === applications * requiredQty,
      scope: "UNIT",
      benefitCode: code,
      appliedApplicationCount: applications,
      coveredUnits: groupCoverageUnits(selected),
    };
  }

  if (code === "CHEAPEST_ITEM_PERCENT_OFF" || code === "CHEAPEST_ITEM_FREE") {
    const pool = String(params.pool_code ?? "");
    const units = isPositiveInteger(params.units);
    if (!pool || !units) return notReadyCoverage(code);
    const rows = coverageUnitRows(lines, targetLineIds(runtime, lines, "POOL", pool));
    if (!rows) return notReadyCoverage(code);
    const selected = [...rows]
      .sort((a, b) => (a.minor - b.minor) || a.key.localeCompare(b.key))
      .slice(0, units);
    return {
      ready: selected.length === units,
      scope: "UNIT",
      benefitCode: code,
      appliedApplicationCount: selected.length === units ? 1 : 0,
      coveredUnits: groupCoverageUnits(selected),
    };
  }

  if (code === "PERCENT_OFF" || code === "FIXED_AMOUNT_OFF" || code === "FIXED_AMOUNT_OFF_PER_UNIT") {
    const target = String(params.target ?? "").toUpperCase();
    const ids = targetLineIds(runtime, lines, target, String(params.pool_code ?? ""));
    const rows = coverageUnitRows(lines, ids);
    if (!rows) return notReadyCoverage(code);
    return {
      ready: rows.length > 0,
      scope: "UNIT",
      benefitCode: code,
      appliedApplicationCount: rows.length > 0 ? 1 : 0,
      coveredUnits: groupCoverageUnits(rows),
    };
  }

  return notReadyCoverage(code || "UNSUPPORTED");
}

function applyBenefit(
  benefit: PromotionBenefit,
  runtime: PromotionRuntimeSnapshot,
  lines: ResolverLine[],
  remaining: Map<string, number>,
  currencyScale: number,
) {
  const code = benefit.code.toUpperCase();
  const params = benefit.params ?? {};
  if (code === "PERCENT_OFF" || code === "FIXED_AMOUNT_OFF") {
    const target = String(params.target ?? "").toUpperCase();
    const ids = targetLineIds(runtime, lines, target, String(params.pool_code ?? ""));
    const available = ids.reduce((sum, id) => sum + (remaining.get(id) ?? 0), 0);
    let desired = code === "PERCENT_OFF"
      ? Math.round(available * positiveNumber(params.percent) / 100)
      : toMinor(positiveNumber(params.amount), currencyScale);
    if (code === "PERCENT_OFF" && params.cap_amount != null) desired = Math.min(desired, toMinor(positiveNumber(params.cap_amount), currencyScale));
    return proportionalAllocate(remaining, ids, desired);
  }

  if (code === "FIXED_AMOUNT_OFF_PER_UNIT") {
    const target = String(params.target ?? "").toUpperCase();
    const ids = targetLineIds(runtime, lines, target, String(params.pool_code ?? ""));
    return fixedAmountPerUnitAllocate(
      lines,
      remaining,
      ids,
      toMinor(positiveNumber(params.amount), currencyScale),
    );
  }

  if (code === "CHEAPEST_ITEM_PERCENT_OFF" || code === "CHEAPEST_ITEM_FREE") {
    const ids = targetLineIds(runtime, lines, "POOL", String(params.pool_code ?? ""));
    const units = isPositiveInteger(params.units);
    if (!units || lines.some((line) => ids.includes(line.lineId) && (!Number.isInteger(line.quantity) || line.quantity > 1000))) {
      throw new Error("NON_INTEGER_QUANTITY_NOT_SUPPORTED");
    }
    const percent = code === "CHEAPEST_ITEM_FREE" ? 100 : positiveNumber(params.percent);
    return cheapestUnitAllocate(lines, remaining, ids, units, percent);
  }

  if (code === "BUY_X_GET_Y") {
    const triggerPool = String(params.trigger_pool_code ?? "");
    const rewardPool = String(params.reward_pool_code ?? "");
    const triggerQty = isPositiveInteger(params.trigger_qty);
    const rewardQty = isPositiveInteger(params.reward_qty);
    if (!triggerQty || !rewardQty) throw new Error("RUNTIME_SCHEMA_NOT_SUPPORTED");
    const relevant = lines.filter((line) => lineMatchesPool(runtime, triggerPool, line) || lineMatchesPool(runtime, rewardPool, line));
    if (relevant.some((line) => !Number.isInteger(line.quantity) || line.quantity > 1000)) {
      throw new Error("NON_INTEGER_QUANTITY_NOT_SUPPORTED");
    }
    const triggerTotal = lines.filter((line) => lineMatchesPool(runtime, triggerPool, line)).reduce((sum, line) => sum + line.quantity, 0);
    const rewardTotal = lines.filter((line) => lineMatchesPool(runtime, rewardPool, line)).reduce((sum, line) => sum + line.quantity, 0);
    let applications = triggerPool.toUpperCase() === rewardPool.toUpperCase()
      ? Math.floor(triggerTotal / (triggerQty + rewardQty))
      : Math.min(Math.floor(triggerTotal / triggerQty), Math.floor(rewardTotal / rewardQty));
    if (params.repeat === false) applications = Math.min(applications, 1);
    const maxApplications = isPositiveInteger(runtime.limit_document?.max_applications_per_order);
    if (maxApplications) applications = Math.min(applications, maxApplications);
    const rewardUnits = Math.max(0, applications) * rewardQty;
    const ids = targetLineIds(runtime, lines, "POOL", rewardPool);
    return cheapestUnitAllocate(lines, remaining, ids, rewardUnits, positiveNumber(params.reward_percent));
  }

  if (code === "BUNDLE_PRICE") {
    const pool = String(params.pool_code ?? "");
    const requiredQty = isPositiveInteger(params.required_qty);
    const bundlePrice = positiveNumber(params.bundle_price);
    if (!pool || !requiredQty || !Number.isFinite(bundlePrice) || bundlePrice < 0 || typeof params.repeat !== "boolean") {
      throw new Error("RUNTIME_SCHEMA_NOT_SUPPORTED");
    }
    const ids = targetLineIds(runtime, lines, "POOL", pool);
    const relevant = lines.filter((line) => ids.includes(line.lineId));
    if (relevant.some((line) => !Number.isInteger(line.quantity) || line.quantity <= 0 || line.quantity > 1000)) {
      throw new Error("NON_INTEGER_QUANTITY_NOT_SUPPORTED");
    }
    const totalUnits = relevant.reduce((sum, line) => sum + line.quantity, 0);
    let applications = Math.floor(totalUnits / requiredQty);
    if (params.repeat === false) applications = Math.min(applications, 1);
    const maxApplications = isPositiveInteger(runtime.limit_document?.max_applications_per_order);
    if (maxApplications) applications = Math.min(applications, maxApplications);
    return bundlePriceAllocate(
      lines,
      remaining,
      ids,
      requiredQty,
      toMinor(bundlePrice, currencyScale),
      Math.max(0, applications),
    );
  }

  throw new Error("RUNTIME_SCHEMA_NOT_SUPPORTED");
}

function capAllocations(allocations: Map<string, number>, capMinor: number) {
  const total = [...allocations.values()].reduce((sum, value) => sum + value, 0);
  if (total <= capMinor) return allocations;
  const remaining = new Map<string, number>([...allocations.entries()]);
  return proportionalAllocate(remaining, [...allocations.keys()], capMinor);
}

function evaluateCandidate(
  runtime: PromotionRuntimeSnapshot,
  lines: ResolverLine[],
  claimedIds: Set<string>,
  activeMember: boolean,
  evaluationAt: Date,
  currencyScale: number,
) {
  const base = {
    policyId: runtime.policy_id,
    policyName: runtime.policy_name,
    promotionVersionId: runtime.promotion_version_id,
  };
  if (![1, 2].includes(runtime.runtime_schema_version) || runtime.rule_schema_version !== 1 || runtime.lifecycle_status !== "PUBLISHED") {
    return { eligible: false as const, reason: "RUNTIME_SCHEMA_NOT_SUPPORTED", ...base };
  }
  if (runtime.effective_from && Date.parse(runtime.effective_from) > evaluationAt.getTime()) return { eligible: false as const, reason: "NOT_STARTED", ...base };
  if (runtime.effective_to && Date.parse(runtime.effective_to) <= evaluationAt.getTime()) return { eligible: false as const, reason: "EXPIRED", ...base };
  if (!scheduleEligible(runtime, evaluationAt)) return { eligible: false as const, reason: "OUTSIDE_SCHEDULE", ...base };
  if (runtime.audience_mode === "ACTIVE_MEMBER_REQUIRED" && !activeMember) {
    return { eligible: false as const, reason: "ACTIVE_MEMBER_REQUIRED", ...base };
  }
  if (runtime.activation_code === "STAFF_CLAIM" && !claimedIds.has(runtime.promotion_version_id)) {
    return { eligible: false as const, reason: "ACTIVATION_REQUIRED", ...base };
  }
  if (runtime.activation_code === "CLAIM_CODE") {
    return { eligible: false as const, reason: "CLAIM_ACTIVATION_REQUIRED", ...base };
  }
  if (runtime.activation_code === "MEMBER_ENTITLEMENT" || runtime.offline_capability === "OFFLINE_RISK_LIMITED") {
    return { eligible: false as const, reason: "RUNTIME_SCHEMA_NOT_SUPPORTED", requiresOnline: runtime.offline_capability !== "OFFLINE_SAFE", ...base };
  }
  if (!conditionMatches(runtime.condition_document, runtime, lines, currencyScale)) return { eligible: false as const, reason: "CONDITION_NOT_MET", ...base };
  if (runtime.offline_capability === "ONLINE_REQUIRED") {
    return { eligible: false as const, reason: "ONLINE_REQUIRED", requiresOnline: true, ...base };
  }
  if (runtime.offline_capability !== "OFFLINE_SAFE") {
    return { eligible: false as const, reason: "RUNTIME_SCHEMA_NOT_SUPPORTED", requiresOnline: true, ...base };
  }

  const remaining = new Map(lines.map((line) => [line.lineId, line.eligibleMinor]));
  const allocations = new Map<string, number>();
  try {
    for (const benefit of runtime.benefit_document ?? []) {
      const next = applyBenefit(benefit, runtime, lines, remaining, currencyScale);
      applyAllocations(remaining, next);
      mergeAllocations(allocations, next);
    }
  } catch (error) {
    return { eligible: false as const, reason: error instanceof Error ? error.message : "RUNTIME_SCHEMA_NOT_SUPPORTED", ...base };
  }

  let finalAllocations = allocations;
  if (runtime.limit_document?.order_discount_cap != null) {
    finalAllocations = capAllocations(allocations, toMinor(positiveNumber(runtime.limit_document.order_discount_cap), currencyScale));
  }
  const requestedMinor = [...finalAllocations.values()].reduce((sum, value) => sum + value, 0);
  if (requestedMinor <= 0) return { eligible: false as const, reason: "NO_ELIGIBLE_AMOUNT", ...base };
  return {
    eligible: true as const,
    candidate: {
      runtime,
      requestedMinor,
      allocations: finalAllocations,
      applicationCoverage: resolveApplicationCoverage(runtime, lines),
    } satisfies Candidate,
  };
}

function positiveAllocationLineIds(candidate: Candidate) {
  return [...candidate.allocations.entries()]
    .filter(([, amount]) => amount > 0)
    .map(([lineId]) => lineId)
    .sort();
}

function chooseCandidates(candidates: Candidate[]) {
  const selected = new Set<string>();
  for (const candidate of candidates) {
    if (candidate.runtime.stacking_mode === "STACKABLE") selected.add(candidate.runtime.promotion_version_id);
  }

  for (const mode of ["EXCLUSIVE", "BEST_DEAL"] as const) {
    const groups = new Map<string, Candidate[]>();
    for (const candidate of candidates.filter((item) => item.runtime.stacking_mode === mode)) {
      // No group means no declared conflict relationship; keep the version isolated.
      const group = candidate.runtime.stack_group_code?.trim() || `VERSION:${candidate.runtime.promotion_version_id}`;
      groups.set(group, [...(groups.get(group) ?? []), candidate]);
    }
    for (const group of groups.values()) {
      group.sort((a, b) => {
        if (mode === "BEST_DEAL" && a.requestedMinor !== b.requestedMinor) return b.requestedMinor - a.requestedMinor;
        if (a.runtime.priority_rank !== b.runtime.priority_rank) return a.runtime.priority_rank - b.runtime.priority_rank;
        return a.runtime.promotion_version_id.localeCompare(b.runtime.promotion_version_id);
      });

      const occupiedLineIds = new Set<string>();
      for (const candidate of group) {
        const candidateLineIds = positiveAllocationLineIds(candidate);
        const overlaps = candidateLineIds.some((lineId) => occupiedLineIds.has(lineId));
        if (overlaps) continue;
        selected.add(candidate.runtime.promotion_version_id);
        candidateLineIds.forEach((lineId) => occupiedLineIds.add(lineId));
      }
    }
  }
  return candidates
    .filter((candidate) => selected.has(candidate.runtime.promotion_version_id))
    .sort((a, b) => (a.runtime.priority_rank - b.runtime.priority_rank) || a.runtime.promotion_version_id.localeCompare(b.runtime.promotion_version_id));
}

export function resolveLocalPromotionsV1(input: {
  cart: LocalCartDraft;
  promotions: PromotionRuntimeSnapshot[];
  promotionRuntimeRevision: number;
  evaluationAt?: Date;
}): LocalPromotionResolution {
  const evaluationAt = input.evaluationAt ?? new Date();
  const claimedIds = new Set(input.cart.claimedPromotionVersionIds);
  const activeMember = Boolean(input.cart.selectedMember);
  const manualClaimSelectionLimit = input.promotions
    .map((promotion) => promotion.manual_claim_selection_limit)
    .filter((value): value is number => typeof value === "number" && Number.isInteger(value) && value > 0)
    .reduce((limit, value) => Math.min(limit, value), 50);
  if (claimedIds.size > manualClaimSelectionLimit) throw new Error("PROMOTION_MANUAL_SELECTION_LIMIT_EXCEEDED");
  const currencyCodes = [...new Set(input.cart.lines.map((line) => line.currencyCode))];
  const currencyScales = [...new Set(input.cart.lines.map((line) => line.currencyScale))];
  if (currencyCodes.length !== 1) throw new Error("LOCAL_PROMOTION_CURRENCY_MISMATCH");
  if (currencyScales.length !== 1 || currencyScales[0] == null || !Number.isInteger(currencyScales[0]) || currencyScales[0] < 0 || currencyScales[0] > 6) {
    throw new Error("LOCAL_CURRENCY_SCALE_NOT_READY");
  }
  const currencyScale = currencyScales[0];
  const lines: ResolverLine[] = input.cart.lines.map((line) => ({
    lineId: line.lineId,
    menuItemId: line.menuItemId,
    menuVariantId: line.menuVariantId,
    menuCategoryId: line.menuCategoryId ?? null,
    variantOptionIds: [...new Set(line.variantOptionIds)],
    modifierGroupIds: [...new Set(line.modifiers.map((modifier) => modifier.modifierGroupId))],
    modifierOptionIds: [...new Set(line.modifiers.map((modifier) => modifier.modifierOptionId))],
    quantity: line.quantity,
    baseUnitPrice: line.baseUnitPrice,
    eligibleMinor: toMinor(line.unitPrice * line.quantity, currencyScale),
  }));

  const blockedReasonCodes: string[] = [];
  const candidates: Candidate[] = [];
  let requiresOnlineResolution = false;
  for (const runtime of input.promotions) {
    const evaluated = evaluateCandidate(runtime, lines, claimedIds, activeMember, evaluationAt, currencyScale);
    if (evaluated.eligible) candidates.push(evaluated.candidate);
    else {
      blockedReasonCodes.push(evaluated.reason);
      if ("requiresOnline" in evaluated && evaluated.requiresOnline) requiresOnlineResolution = true;
    }
  }

  const selected = chooseCandidates(candidates);
  const lineRemaining = new Map(lines.map((line) => [line.lineId, line.eligibleMinor]));
  const appliedPromotions: LocalAppliedPromotion[] = [];
  for (const candidate of selected) {
    let appliedMinor = 0;
    const actualLineAllocations = new Map<string, number>();
    for (const [lineId, requested] of [...candidate.allocations.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      const available = lineRemaining.get(lineId) ?? 0;
      const applied = Math.min(requested, available);
      if (applied > 0) {
        lineRemaining.set(lineId, available - applied);
        appliedMinor += applied;
        actualLineAllocations.set(lineId, (actualLineAllocations.get(lineId) ?? 0) + applied);
      }
    }
    if (appliedMinor > 0) {
      appliedPromotions.push({
        promotionPolicyId: candidate.runtime.policy_id,
        promotionVersionId: candidate.runtime.promotion_version_id,
        policyCode: candidate.runtime.policy_code,
        policyName: candidate.runtime.policy_name,
        activationCode: candidate.runtime.activation_code,
        appliedDiscountAmount: fromMinor(appliedMinor, currencyScale),
        loyaltyEarnTreatment: candidate.runtime.loyalty_earn_treatment ?? "FULL_POINTS",
        conditionDocument: candidate.runtime.condition_document as Record<string, unknown>,
        benefitDocument: candidate.runtime.benefit_document,
        applicationCoverage: candidate.applicationCoverage,
        lineAllocations: [...actualLineAllocations.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([lineId, amount]) => ({
            lineId,
            amount: fromMinor(amount, currencyScale),
            allocationPrecision: "LINE" as const,
          })),
        offlineCapability: candidate.runtime.offline_capability,
      });
    }
  }

  const preMinor = lines.reduce((sum, line) => sum + line.eligibleMinor, 0);
  const postMinor = [...lineRemaining.values()].reduce((sum, value) => sum + value, 0);
  const discountMinor = preMinor - postMinor;
  const staffClaimOptions: LocalStaffClaimPromotion[] = input.promotions
    .filter((runtime) => runtime.activation_code === "STAFF_CLAIM" && runtime.lifecycle_status === "PUBLISHED")
    .filter((runtime) => !runtime.effective_from || Date.parse(runtime.effective_from) <= evaluationAt.getTime())
    .filter((runtime) => !runtime.effective_to || Date.parse(runtime.effective_to) > evaluationAt.getTime())
    .filter((runtime) => scheduleEligible(runtime, evaluationAt))
    .filter((runtime) => runtime.audience_mode !== "ACTIVE_MEMBER_REQUIRED" || activeMember)
    .map((runtime) => ({
      promotionPolicyId: runtime.policy_id,
      promotionVersionId: runtime.promotion_version_id,
      policyCode: runtime.policy_code,
      policyName: runtime.policy_name,
      offlineCapability: runtime.offline_capability,
    }));

  const cartFingerprint = localCartSaleFingerprint(input.cart);
  return {
    quote: {
      quoteFingerprint: `LOCAL_PROMO_V1:${input.promotionRuntimeRevision}:${cartFingerprint}`,
      evaluatedAt: evaluationAt.toISOString(),
      currencyCode: input.cart.lines[0]?.currencyCode ?? "THB",
      prePromotionTotal: fromMinor(preMinor, currencyScale),
      promotionDiscountTotal: fromMinor(discountMinor, currencyScale),
      postPromotionTotal: fromMinor(postMinor, currencyScale),
      appliedPromotions,
      staffClaimOptions,
      promotionRuntimeRevision: input.promotionRuntimeRevision,
      quoteSource: "LOCAL_PROMOTION_RUNTIME_V1",
      requiresOnlineResolution,
    },
    blockedReasonCodes,
    requiresOnlineResolution,
  };
}